/**
 * Phase2BoardCell.tsx
 * The COMPACT (Board-view) presentation of one Task×Week cell. Pixels only — every
 * behavior comes from useWeeklyCellModel, shared verbatim with the Classic card
 * (Phase2WeeklyCell), so the two views can't drift.
 *
 * Per machine booking: a reused MachineBar header (no resize handle, no BOOKED chip —
 * the "+ add" lives in the bar's header slot). Below it, employees flow ~2 per row
 * (Option A): a larger photo/initials avatar + (full) name. Wave 3 (percent
 * retirement): the draggable WorkloadBar % is GONE — an assignee is a presence
 * (busy day), the server pins Percent=100. Off-week cells render blank (no "—")
 * so the aligned grid declutters.
 */

import React from 'react';
import { Alert, Box, IconButton, Snackbar, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { alpha, useTheme } from '@mui/material/styles';

import { MachineBar } from './Phase2MachineBar';
import { taskWorkLabel } from '../../../../utils/bookingLabel';
import { useFlashUser } from './flashUserContext';
import { InlineAssigneePicker } from '../../tasks/InlineAssigneePicker';
import { InitialsAvatar } from '../../../../components/schedule/InitialsAvatar';
import { useWeeklyCellModel, TASK_LEVEL_PICKER } from './useWeeklyCellModel';
import { ConfirmDialog } from '../../../../components/shared/ConfirmDialog';
import type { Phase2WeeklyCellProps } from './Phase2WeeklyCell';
import type { TaskAssignee } from '../../../../services/scheduleTasks';

// Board cell takes the SAME props as the Classic cell, plus a Graph photo lookup.
export type Phase2BoardCellProps = Phase2WeeklyCellProps & {
  /** email → Graph photo blob URL (or null). From useGraphPhotos in the grid. */
  photoByEmail?: Map<string, string | null>;
};

// ---------------------------------------------------------------------------
// Employee row — avatar (photo) + name (presence, Option A, 2 per row)
// ---------------------------------------------------------------------------

interface BoardEmployeeRowProps {
  assignee: TaskAssignee;
  isRemoving: boolean;
  onRemove: () => void;
  showBaseline: boolean;
  /** Resolved Graph photo URL (or null → initials). */
  photoUrl?: string | null;
}

const BoardEmployeeRow: React.FC<BoardEmployeeRowProps> = ({
  assignee,
  isRemoving,
  onRemove,
  showBaseline,
  photoUrl,
}) => {
  const theme = useTheme();
  const flashUser = useFlashUser();
  const committed = assignee.Status === 'Committed';
  const tip = assignee.displayName + (showBaseline ? ' · also in baseline' : '');
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0, opacity: isRemoving ? 0.4 : 1, transition: 'opacity 0.15s' }}>
      {/* Avatar + name → flash this person in the capacity grid below (2026-06-25). */}
      <Box
        onClick={() => flashUser(assignee.UserKey)}
        title="Show this person's days & % in the capacity grid below"
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0, flex: 1,
          cursor: 'pointer', '&:hover .flashName': { textDecoration: 'underline' },
        }}
      >
        <InitialsAvatar displayName={assignee.displayName} seed={assignee.UserKey} photoUrl={photoUrl ?? assignee.photoUrl} size={30} />
        <Tooltip title={tip} arrow enterDelay={500}>
          <Typography
            className="flashName"
            variant="caption"
            noWrap
            sx={{ fontSize: '0.8rem', fontWeight: 500, color: 'text.primary', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {assignee.displayName}
          </Typography>
        </Tooltip>
      </Box>
      {!committed && (
        <Tooltip title="Draft — being added in this plan" arrow>
          <Box
            component="span"
            sx={{
              flexShrink: 0, px: 0.35, borderRadius: '3px', fontSize: '0.46rem', fontWeight: 800,
              letterSpacing: '0.04em', lineHeight: 1.6, color: theme.schedule.employeeAlloc,
              backgroundColor: alpha(theme.schedule.employeeAlloc, 0.16),
            }}
          >
            NEW
          </Box>
        </Tooltip>
      )}
      <Tooltip title="Remove from this week">
        <span>
          <IconButton size="small" disabled={isRemoving} onClick={onRemove} sx={{ p: 0, color: 'text.disabled', '&:hover': { color: 'error.main' } }}>
            <CloseIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const Phase2BoardCell: React.FC<Phase2BoardCellProps> = ({
  task,
  weekStart,
  assignments,
  scenarioKey,
  onAssignmentsChanged,
  onAllocationChanged,
  onEscalateToDialog,
  baselineAssignments,
  compareToBaseline,
  photoByEmail,
  holidays,
}) => {
  const theme = useTheme();
  const m = useWeeklyCellModel({
    task, weekStart, assignments, onAssignmentsChanged, onAllocationChanged, baselineAssignments, compareToBaseline,
    // W2-8: scopes the ×-remove stranding check to this plan's overlay.
    scenarioKey: scenarioKey ?? undefined,
    // Holiday-aware continuous staffing-window weeks (full duration — Q2).
    holidays,
  });

  /** Employee rows — flow ~2 per line (1 gets the full width → full name). */
  const renderRows = (rows: TaskAssignee[]) =>
    rows.length > 0 ? (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '6px 14px' }}>
        {rows.map((a) => (
          <BoardEmployeeRow
            key={a.EmployeeAssignmentKey}
            assignee={a}
            photoUrl={photoByEmail?.get(a.email)}
            isRemoving={m.isRemoving(a)}
            onRemove={() => m.requestRemove(a.EmployeeAssignmentKey, a.displayName, a.UserKey)}
            showBaseline={m.showBaseline(a)}
          />
        ))}
      </Box>
    ) : null;

  /** The "+" add-employee control (icon for the header, labelled for the task-level group). */
  const addButton = (groupId: string, labelled = false) => (
    <Tooltip title="Add an employee for this week" arrow>
      {labelled ? (
        <Box
          component="button"
          type="button"
          onClick={() => m.setPickerFor(groupId)}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.3, alignSelf: 'flex-start',
            px: 0.6, py: 0.2, border: `1px dashed ${theme.schedule.employeeAlloc}`, borderRadius: '5px',
            backgroundColor: alpha(theme.schedule.employeeAlloc, 0.08), color: theme.schedule.employeeAlloc,
            fontSize: '0.62rem', fontWeight: 700, lineHeight: 1.2, cursor: 'pointer',
            '&:hover': { backgroundColor: alpha(theme.schedule.employeeAlloc, 0.2), borderStyle: 'solid' },
          }}
        >
          <AddIcon sx={{ fontSize: 13 }} />
          Add employee
        </Box>
      ) : (
        <IconButton
          size="small"
          onClick={() => m.setPickerFor(groupId)}
          sx={{
            flexShrink: 0, p: 0.3, color: theme.schedule.employeeAlloc,
            border: `1px dashed ${alpha(theme.schedule.employeeAlloc, 0.6)}`, borderRadius: '6px',
            backgroundColor: alpha(theme.schedule.employeeAlloc, 0.08),
            '&:hover': { backgroundColor: alpha(theme.schedule.employeeAlloc, 0.2), borderStyle: 'solid' },
          }}
        >
          <AddIcon sx={{ fontSize: 15 }} />
        </IconButton>
      )}
    </Tooltip>
  );

  /** The inline add picker for a group — renders at the bottom of the card when open. */
  const picker = (groupId: string, machineBookingKey: string | null, groupAssignees: TaskAssignee[]) =>
    m.pickerFor === groupId ? (
      <Box sx={{ mt: 0.25 }}>
        <InlineAssigneePicker
          task={task}
          assignees={groupAssignees}
          machineBookingKey={machineBookingKey}
          onAssigned={m.onPickerAssigned}
          onUnassigned={m.onPickerUnassigned}
          onEscalateToDialog={onEscalateToDialog}
          scenarioKey={scenarioKey ?? undefined}
          targetWeekStart={weekStart}
        />
      </Box>
    ) : null;

  // Off-week / not scheduled — render BLANK (no "—") so the aligned grid declutters.
  if (m.isEmpty) {
    return <Box aria-hidden sx={{ minHeight: 6 }} />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.5, px: 0.4 }}>
      {m.machineGroups.map(({ seg, weekAssignees, anchorKeys, under, midRunContinuous }) => (
        <Box
          key={seg.key}
          sx={{
            display: 'flex', flexDirection: 'column', gap: 0.4, p: 0.5, borderRadius: '7px',
            border: `1px solid ${under ? theme.schedule.maintenance : alpha(theme.schedule.machineTime, 0.22)}`,
            borderLeftWidth: under ? 4 : 1,
            backgroundColor: under ? alpha(theme.schedule.maintenance, 0.05) : alpha(theme.palette.background.paper, 0.6),
          }}
        >
          {/* Machine bar — no resize handle, no BOOKED chip; "+" lives in the bar.
              Lab answer Q2 (2026-07-10): continuous staffs the full duration, so
              mid-run weeks keep the "+" — it's only hidden for a week with no
              staffable working day (the backend refuses those). */}
          <MachineBar
            bookingKey={seg.key}
            bookingStart={seg.bookingStart}
            bookingEnd={seg.bookingEnd}
            displayCode={seg.code}
            displayName={seg.name}
            effectiveStart={seg.effectiveStart}
            effectiveEnd={seg.effectiveEnd}
            days={seg.days}
            status={seg.status}
            assignmentKeys={anchorKeys}
            onResized={m.onBookingResized}
            hideResize
            hideStatus
            headerAction={m.pickerFor !== seg.key && !midRunContinuous ? addButton(seg.key) : undefined}
            holidays={holidays}
            workLabel={taskWorkLabel(task)}
            runMode={task.RunMode ?? null}
          />
          {/* Only flag the one thing that matters — nobody assigned this week. Once at least
              one person is on it, no nag. Gigi 2026-06-25. Lab answer Q2 (2026-07-10):
              continuous staffs the FULL duration, so mid-run weeks get the normal
              amber cue; the calm caption only remains for a week with no staffable
              working day. */}
          {midRunContinuous && (
            <Typography variant="caption" sx={{ fontSize: '0.56rem', color: 'text.secondary', px: 0.25 }} noWrap>
              Runs 24/7 — no working days to staff this week
            </Typography>
          )}
          {under && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, px: 0.25 }}>
              <WarningAmberIcon sx={{ fontSize: 11, color: theme.schedule.maintenance }} />
              <Typography variant="caption" sx={{ fontSize: '0.58rem', fontWeight: 700, color: theme.schedule.maintenance }} noWrap>
                Unstaffed — add someone
              </Typography>
            </Box>
          )}
          {renderRows(weekAssignees)}
          {!midRunContinuous && picker(seg.key, seg.key, weekAssignees)}
        </Box>
      ))}

      {/* Legacy task-level group (≥2 machines, such rows exist). */}
      {m.taskLevelAssignees.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
          <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.disabled', fontStyle: 'italic' }}>
            Whole task (not machine-specific)
          </Typography>
          {renderRows(m.taskLevelAssignees)}
        </Box>
      )}

      {/* Task-level "+" only when there's no machine in this week to staff. */}
      {m.machineGroups.length === 0 && (
        <>
          {m.pickerFor !== TASK_LEVEL_PICKER && addButton(TASK_LEVEL_PICKER, true)}
          {picker(TASK_LEVEL_PICKER, null, m.weekAssignments)}
        </>
      )}

      {/* Confirm before removing an assignee (shared across views). */}
      {m.removeConfirmState.open && <ConfirmDialog state={m.removeConfirmState} onClose={m.cancelRemove} />}

      {/* P-17a: failed remove writes are no longer silent — red snackbar. */}
      <Snackbar open={Boolean(m.saveError)} autoHideDuration={6000} onClose={m.clearSaveError}>
        <Alert severity="error" onClose={m.clearSaveError}>{m.saveError}</Alert>
      </Snackbar>
    </Box>
  );
};
